import { FormControl, FormErrorMessage, Flex } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import { Supply } from "../../types/supplies.types";
import FileInput from "../FileInput";
import Select from "../inputs/Select";
import { useTranslation } from "react-i18next";

type SupplyFormProps = {
  value?: Supply;
};

export type SupplyFormHandler = {
  values: () => Partial<Supply> & { files: FileList };
  valid: () => Promise<boolean>;
};

const SupplyForm = forwardRef<SupplyFormHandler, SupplyFormProps>(
  ({ value }: SupplyFormProps, ref) => {
    const { t } = useTranslation()
    const methods = useForm();

    useImperativeHandle(ref, () => ({
      valid: () => {
        return methods.trigger();
      },
      values: () => {
        const values = methods.getValues()
        return {
          label: values.label,
          description: values.description,
          price: Number.parseFloat(values.price) * 100,
          stock: Number.parseInt(values.stock) ?? 0,
          sageId: values.sageId,
          status: values.status,
          files: values.files
        }
      },
    }));
    return (
      <FormProvider {...methods}>
        <form style={{ marginTop: "32px" }}>
          <Flex direction="column" gap="24px">
            <FormControl isInvalid={!!methods.formState.errors.label}>
              <Input
                id="label"
                placeholder="Titre"
                defaultValue={value?.label ?? ""}
                {...methods.register("label", {
                  required: "Ce champ est obligatoire",
                })}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.label?.message}</>
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!methods.formState.errors.description}>
              <Input
                id="description"
                placeholder="Description"
                defaultValue={value?.description ?? ""}
                {...methods.register("description")}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.description?.message}</>
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!methods.formState.errors.sageId}>
              <Input
                id="sageId"
                placeholder="Sage ID"
                defaultValue={value?.sageId ?? ""}
                {...methods.register("sageId")}
              ></Input>
              <FormErrorMessage>
                <>{methods.formState.errors?.sageId?.message}</>
              </FormErrorMessage>
            </FormControl>

            <Flex justifyContent={"space-between"} gap={4}>
              <FormControl isInvalid={!!methods.formState.errors.price}>
                <Input
                  id="price"
                  placeholder="Prix à l'unité"
                  defaultValue={value?.price ? value.price/100 : ""}
                  unit='€'
                  type={"number"}
                  {...methods.register("price", {
                    required: "Ce champ est obligatoire",
                  })}
                ></Input>
                <FormErrorMessage>
                  <>{methods.formState.errors?.price?.message}</>
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!methods.formState.errors.stock}>
                <Input
                  id="stock"
                  placeholder="Inventaire"
                  defaultValue={value?.stock ?? ""}
                  type={"number"}
                  {...methods.register("stock", {
                    required: "Ce champ est obligatoire",
                  })}
                ></Input>
                <FormErrorMessage>
                  <>{methods.formState.errors?.stock?.message}</>
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!methods.formState.errors.status}>
                <Select
                  id="status"
                  placeholder="Statut"
                  defaultValue={value?.status ?? "DRAFT"}
                  {...methods.register("status", {
                    required: "Ce champ est obligatoire",
                  })}
                >
                    <option value="DRAFT">{t('supply.status.DRAFT')}</option>
                    <option value="INACTIVE">{t('supply.status.INACTIVE')}</option>
                    <option value="ACTIVE">{t('supply.status.ACTIVE')}</option>
                </Select>
                <FormErrorMessage>
                  <>{methods.formState.errors?.status?.message}</>
                </FormErrorMessage>
              </FormControl>
            </Flex>

            <FormControl isInvalid={!!methods.formState.errors.files}>
              <FileInput {...methods.register("files")}></FileInput>
              <FormErrorMessage>
                <>{methods.formState.errors?.files?.message}</>
              </FormErrorMessage>
            </FormControl>
          </Flex>
        </form>
      </FormProvider>
    );
  }
);

export default SupplyForm;
